import { batch, signal } from '@preact/signals'
import type { ReadonlySignal, Signal } from '@preact/signals'

export type StoreOp<K, V, M> =
  | { t: 'upsert'; value: V }
  | { t: 'remove'; key: K }
  | { t: 'meta'; value: M }

/** JSON object property order is immaterial. */
function equal(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object' || Array.isArray(a) !== Array.isArray(b)) return false
  const ak = Object.keys(a), bk = Object.keys(b)
  return ak.length === bk.length && ak.every(k => Object.hasOwn(b, k) && equal((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]))
}

/** Facts are replaced, never mutated. The view owns display order; this store owns per-key subscriptions only. */
export function keyedStore<K, V, M>(keyOf: (value: V) => K, initialMeta: M) {
  const cells = new Map<K, Signal<Readonly<V> | undefined>>()
  const subscriptions = new Map<K, Set<() => void>>()
  const missing = signal<Readonly<V> | undefined>(undefined), meta = signal<Readonly<M>>(initialMeta)
  const put = (value: V) => {
    const key = keyOf(value), cell = cells.get(key)
    if (!cell) cells.set(key, signal(value))
    else if (!equal(cell.peek(), value)) cell.value = value
  }
  const remove = (key: K) => {
    for (const stop of subscriptions.get(key) ?? []) stop()
    subscriptions.delete(key)
    const cell = cells.get(key)
    cells.delete(key)
    if (cell) cell.value = undefined
  }
  return {
    meta: meta as ReadonlySignal<Readonly<M>>,
    item: (key: K): ReadonlySignal<Readonly<V> | undefined> => cells.get(key) ?? missing,
    snapshot(values: readonly V[], metadata?: M) {
      const wanted = new Set(values.map(keyOf))
      if (wanted.size !== values.length) throw new Error('Duplicate store key')
      batch(() => {
        for (const key of cells.keys()) if (!wanted.has(key)) remove(key)
        values.forEach(put)
        if (metadata !== undefined && !equal(meta.peek(), metadata)) meta.value = metadata
      })
    },
    apply(ops: readonly StoreOp<K, V, M>[]) {
      const changes = new Map<K, V | undefined>(), removed = new Set<K>()
      let metadata = meta.peek() as M
      for (const op of ops) {
        if (op.t === 'upsert') changes.set(keyOf(op.value), op.value)
        else if (op.t === 'remove') { removed.add(op.key); changes.set(op.key, undefined) }
        else metadata = op.value
      }
      batch(() => {
        for (const key of removed) remove(key)
        for (const value of changes.values()) if (value !== undefined) put(value)
        if (!equal(meta.peek(), metadata)) meta.value = metadata
      })
    },
    subscribe(key: K, fn: (value: Readonly<V> | undefined) => void): () => void {
      const cell = cells.get(key)
      if (!cell) return () => {}
      const owned = subscriptions.get(key) ?? new Set<() => void>()
      subscriptions.set(key, owned)
      let release = () => {}, live = true
      const stop = () => {
        if (!live) return
        live = false; release(); owned.delete(stop)
        if (!owned.size) subscriptions.delete(key)
      }
      owned.add(stop)
      const unsubscribe = cell.subscribe(value => { if (live) fn(value) })
      if (live) release = unsubscribe
      else unsubscribe()
      return stop
    },
    dispose() { batch(() => { for (const key of [...cells.keys()]) remove(key) }) },
  }
}
